<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  title: { type: String, default: '' },
  columns: {
    type: Array,
    required: true,
    // Each: { key, label, align, type, width } type: 'text'|'badge'|'trend'|'mono'
  },
  rows: { type: Array, required: true },
  perPage: { type: Number, default: 25 },
  // GK-HUB: Optional row-click support for drill-downs
  clickableRows: { type: Boolean, default: false },
});

// GK-HUB: Emit row-click when clickable-rows is enabled
const emit = defineEmits(['row-click']);

const { t } = useI18n();

const sortKey = ref('');
const sortDir = ref('asc');
const currentPage = ref(1);

// Reset to page 1 when rows change (new data / filters)
watch(
  () => props.rows,
  () => {
    currentPage.value = 1;
  }
);

const toggleSort = key => {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortDir.value = 'desc';
  }
};

const sortedRows = computed(() => {
  if (!sortKey.value) return props.rows;
  return [...props.rows].sort((a, b) => {
    const av = a[sortKey.value];
    const bv = b[sortKey.value];
    const numA = typeof av === 'string' ? parseFloat(av) : av;
    const numB = typeof bv === 'string' ? parseFloat(bv) : bv;
    if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
      return sortDir.value === 'asc' ? numA - numB : numB - numA;
    }
    const strA = String(av);
    const strB = String(bv);
    return sortDir.value === 'asc'
      ? strA.localeCompare(strB)
      : strB.localeCompare(strA);
  });
});

// Pagination
const totalPages = computed(() => {
  if (!props.perPage || props.perPage <= 0) return 1;
  return Math.max(1, Math.ceil(sortedRows.value.length / props.perPage));
});

const paginatedRows = computed(() => {
  if (!props.perPage || props.perPage <= 0) return sortedRows.value;
  const start = (currentPage.value - 1) * props.perPage;
  return sortedRows.value.slice(start, start + props.perPage);
});

const showingFrom = computed(() =>
  sortedRows.value.length === 0
    ? 0
    : (currentPage.value - 1) * props.perPage + 1
);
const showingTo = computed(() =>
  Math.min(currentPage.value * props.perPage, sortedRows.value.length)
);

const hasPagination = computed(
  () => props.perPage > 0 && sortedRows.value.length > props.perPage
);

// GK-HUB: Badge color thresholds work for both CSAT notes (1-5) and percentages (0-100)
const badgeClass = value => {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return 'bg-n-slate-5/20 text-n-slate-11';
  // Detect CSAT note scale (1-5) vs percentage scale (0-100)
  if (num <= 5) {
    // CSAT note: 4+ teal, 3+ blue, 2+ amber, <2 ruby
    if (num >= 4) return 'bg-n-teal-3 text-n-teal-11';
    if (num >= 3) return 'bg-n-blue-3 text-n-blue-11';
    if (num >= 2) return 'bg-amber-500/12 text-amber-500';
    return 'bg-n-ruby-3 text-n-ruby-11';
  }
  // Percentage scale
  if (num >= 80) return 'bg-n-teal-3 text-n-teal-11';
  if (num >= 60) return 'bg-n-blue-3 text-n-blue-11';
  if (num >= 40) return 'bg-amber-500/12 text-amber-500';
  return 'bg-n-ruby-3 text-n-ruby-11';
};

const trendClass = value => {
  const str = String(value);
  if (str.startsWith('+') || str.startsWith('-')) {
    const num = parseFloat(str);
    if (num > 0) return 'text-n-teal-11';
    if (num < 0) return 'text-n-ruby-11';
  }
  return 'text-n-slate-9';
};
</script>

<template>
  <div class="mb-7">
    <div v-if="title" class="text-[13px] font-semibold text-n-slate-12 mb-4">
      {{ title }}
    </div>
    <div
      class="bg-n-solid-2 border border-n-weak rounded-[10px] overflow-hidden"
    >
      <div class="overflow-x-auto">
        <table class="w-full border-collapse min-w-[600px]">
          <colgroup>
            <col
              v-for="col in columns"
              :key="'cg-' + col.key"
              :style="col.width ? { width: col.width } : {}"
            />
          </colgroup>
          <thead>
            <tr>
              <th
                v-for="col in columns"
                :key="col.key"
                class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-9 px-4 py-3 border-b border-n-weak bg-n-solid-3 cursor-pointer select-none"
                :class="{ 'text-right': col.align === 'right' }"
                @click="toggleSort(col.key)"
              >
                {{ col.label }}
                <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
                <span v-if="sortKey === col.key" class="ml-0.5">
                  {{ sortDir === 'asc' ? '\u2191' : '\u2193' }}
                </span>
                <!-- eslint-enable @intlify/vue-i18n/no-raw-text -->
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in paginatedRows"
              :key="idx"
              class="hover:bg-n-alpha-1 transition-colors"
              :class="{ 'cursor-pointer': clickableRows }"
              @click="clickableRows && emit('row-click', row)"
            >
              <td
                v-for="col in columns"
                :key="col.key"
                class="text-[13px] text-n-slate-12 px-4 py-2.5 border-b border-n-weak last:border-b-0"
                :class="{
                  'text-right': col.align === 'right',
                  'font-mono text-xs': col.type === 'mono',
                }"
              >
                <template v-if="col.type === 'badge'">
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold"
                    :class="badgeClass(row[col.key])"
                  >
                    {{ row[col.key] }}
                  </span>
                </template>
                <template v-else-if="col.type === 'trend'">
                  <span
                    class="text-xs font-medium"
                    :class="trendClass(row[col.key])"
                  >
                    {{ row[col.key] }}
                  </span>
                </template>
                <!-- GK-HUB: 'agent' type renders the name with optional IA badge -->
                <template v-else-if="col.type === 'agent'">
                  <span class="inline-flex items-center gap-1.5 min-w-0">
                    <span class="truncate">{{ row[col.key] }}</span>
                    <span
                      v-if="row.is_ia_agent"
                      class="gk-ia-badge flex-shrink-0"
                    >
                      {{ $t('CONVERSATION.IA_BADGE') }}
                    </span>
                  </span>
                </template>
                <template v-else>
                  {{ row[col.key] }}
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- Pagination -->
      <div
        v-if="hasPagination"
        class="flex items-center justify-between px-4 py-2.5 border-t border-n-weak bg-n-solid-3 text-[12px] text-n-slate-10"
      >
        <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
        <span>
          {{
            t('ISP_REPORTS.PAGINATION.SHOWING', {
              from: showingFrom,
              to: showingTo,
              total: sortedRows.length,
            })
          }}
        </span>
        <div class="flex items-center gap-2">
          <button
            class="px-2.5 py-1 rounded border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            :disabled="currentPage <= 1"
            @click="currentPage--"
          >
            {{ t('ISP_REPORTS.PAGINATION.PREVIOUS') }}
          </button>
          <span class="text-n-slate-11">
            {{
              t('ISP_REPORTS.PAGINATION.PAGE_OF', {
                page: currentPage,
                pages: totalPages,
              })
            }}
          </span>
          <button
            class="px-2.5 py-1 rounded border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            :disabled="currentPage >= totalPages"
            @click="currentPage++"
          >
            {{ t('ISP_REPORTS.PAGINATION.NEXT') }}
          </button>
        </div>
        <!-- eslint-enable @intlify/vue-i18n/no-raw-text -->
      </div>
    </div>
  </div>
</template>
